'use strict';

/**
 * CommonJS mirror of `src/services/ProcedureTeamContextService.js`.
 *
 * KEEP IN SYNC: the two implementations must produce identical output for
 * every (event, procedure) input. The mirror exists only because Firebase
 * Functions' `functions/tsconfig.json` does not expose the `@services` path
 * alias, so CommonJS consumers under `functions/common/` cannot require the
 * ES-module service directly. Any change to resolution rules, field set, or
 * flag semantics must land in both files and be covered by tests on both
 * sides.
 *
 * Unification is tracked by #759.
 */

const {isPerProcedureTeamEnabled} = require('./data/InsuranceHelpers');

/**
 * @typedef {Object} ProcedureTeamContext
 * @property {Array} actors - Team members/actors resolved for the current context.
 * @property {string|null} accommodation - Resolved accommodation for this context. Null in event-level mode; tab.accommodation in tab mode.
 * @property {*} dtProcedure - Procedure date, preserved as-is on the event-level
 *   flag-OFF path (may be undefined); null on nullish-event fallback.
 * @property {string|null} anesthesiaStart - Anesthesia start time or null.
 * @property {string|null} anesthesiaEnd - Anesthesia end time or null.
 * @property {string|null} procedureStart - Procedure start time or null.
 * @property {string|null} procedureEnd - Procedure end time or null.
 * @property {string|null} tabRowId - Identifier of the resolved tab, or null in event-level mode.
 * @property {boolean} isDefault - True when the resolved tab is the default tab.
 * @property {boolean} isTabMode - True when the context is sourced from a procedureTeamTab.
 */

/**
 * Picks a value from the given source or returns null when the source is
 * missing or the key is undefined. Used to normalise optional date/time fields
 * from either an event or a ProcedureTeamTab.
 *
 * @param {Object|null|undefined} source - Object to read from.
 * @param {string} key - Property name to extract.
 * @returns {*} The value at `source[key]` or null when unavailable.
 */
function pickOrNull(source, key) {
  if (source == null) {
    return null;
  }
  const value = source[key];
  return value === undefined ? null : value;
}

/**
 * Resolves the ProcedureTeamTab for a given event/procedure pair.
 *
 * Mirrors `@services/ProcedureTeamTabService.getProcedureTab`. Re-implemented
 * here because Firebase Functions' tsconfig does not expose the `@services`
 * path alias, so the ES module cannot be required from CommonJS consumers.
 *
 * Resolution order:
 * 1. event or its procedureTeamTabs missing/empty -> null
 * 2. procedure or procedureTeamId missing -> default tab (or null)
 * 3. tab whose rowId matches procedure.procedureTeamId -> that tab
 * 4. otherwise -> default tab (or null)
 *
 * @param {Object|null|undefined} event - The event containing procedureTeamTabs.
 * @param {Object|null|undefined} procedure - The procedure with optional procedureTeamId.
 * @returns {Object|null} The resolved tab, or null when resolution is impossible.
 */
function getProcedureTab(event, procedure) {
  if (event == null) {
    return null;
  }

  const tabs = event.procedureTeamTabs;
  if (!Array.isArray(tabs) || tabs.length === 0) {
    return null;
  }

  if (procedure == null || !procedure.procedureTeamId) {
    return tabs.find((t) => t?.isDefault === true) || null;
  }

  const matched = tabs.find((t) => t?.rowId === procedure.procedureTeamId);
  if (matched) {
    return matched;
  }

  return tabs.find((t) => t?.isDefault === true) || null;
}

/**
 * Builds a context sourced from event-level fields. Applied when the feature
 * flag is OFF, when the event has no usable tabs, or when the event itself
 * is null/undefined.
 *
 * Zero-diff contract: when `event` is a real object, `dtProcedure` is
 * preserved AS-IS (including `undefined`). The legacy call site forwarded
 * `event.dtProcedure` directly to `getNegotiation`; coercing `undefined` to
 * `null` here would change downstream moment() semantics and break parity.
 *
 * @param {Object|null|undefined} event - The event to source fields from.
 * @returns {ProcedureTeamContext} A non-frozen context ready to be sealed.
 */
function buildEventLevelContext(event) {
  if (event == null) {
    return {
      actors: [],
      accommodation: null,
      dtProcedure: null,
      anesthesiaStart: null,
      anesthesiaEnd: null,
      procedureStart: null,
      procedureEnd: null,
      tabRowId: null,
      isDefault: false,
      isTabMode: false,
    };
  }

  const actors = Array.isArray(event.multAdditionalData)
    ? event.multAdditionalData
    : [];

  return {
    actors,
    accommodation: pickOrNull(event, 'accommodation'),
    // Reason: flag-OFF zero-diff requires preserving event.dtProcedure AS-IS.
    // moment(undefined) is valid (now); moment(null) is invalid. Do not coerce.
    dtProcedure: event.dtProcedure,
    anesthesiaStart: pickOrNull(event, 'anesthesiaStart'),
    anesthesiaEnd: pickOrNull(event, 'anesthesiaEnd'),
    procedureStart: pickOrNull(event, 'procedureStart'),
    procedureEnd: pickOrNull(event, 'procedureEnd'),
    tabRowId: null,
    isDefault: false,
    isTabMode: false,
  };
}

/**
 * Builds a context sourced from a resolved ProcedureTeamTab. Applied when the
 * feature flag is ON and a tab was matched (either explicitly or via default).
 *
 * The tab's `dtProcedure` falls back to the event-level `dtProcedure` when the
 * tab has no date of its own, keeping the resolved context usable even for
 * tabs that only scope actors.
 *
 * @param {Object} tab - The resolved ProcedureTeamTab.
 * @param {Object} event - The owning event (used for dtProcedure fallback).
 * @returns {ProcedureTeamContext} A non-frozen context ready to be sealed.
 */
function buildTabContext(tab, event) {
  const actors = Array.isArray(tab.actors) ? tab.actors : null;
  const tabDt = tab.dtProcedure;

  return {
    actors,
    accommodation: pickOrNull(tab, 'accommodation'),
    dtProcedure: tabDt != null ? tabDt : event.dtProcedure,
    anesthesiaStart: pickOrNull(tab, 'anesthesiaStart'),
    anesthesiaEnd: pickOrNull(tab, 'anesthesiaEnd'),
    procedureStart: pickOrNull(tab, 'procedureStart'),
    procedureEnd: pickOrNull(tab, 'procedureEnd'),
    tabRowId: tab.rowId ?? null,
    isDefault: tab.isDefault === true,
    isTabMode: true,
  };
}

/**
 * Resolves the rendering context for a given procedure within an event.
 *
 * CommonJS mirror of `src/services/ProcedureTeamContextService.js`. Exposed
 * here so Firebase Functions / CommonJS consumers (notably
 * `EventUtils.setValueProc`) can switch from `event.dtProcedure` to
 * `ctx.dtProcedure` without reaching into the ES services layer.
 *
 * Resolution rules:
 * 1. `event == null` -> neutral context with nulls.
 * 2. Flag OFF -> event-level context; `dtProcedure` preserved AS-IS to keep
 *    flag-OFF zero-diff parity with the legacy call site.
 * 3. Flag ON + no usable tabs (missing or empty array) -> event-level fallback.
 * 4. Flag ON + tab matched by `procedure.procedureTeamId` -> tab context.
 * 5. Flag ON + unmatched/missing procedureTeamId -> default tab context.
 * 6. Flag ON + no default tab at all -> event-level fallback.
 *
 * The returned object is always frozen so callers cannot mutate it.
 *
 * @param {Object|null|undefined} event - The event containing flags and optional tabs.
 * @param {Object|null|undefined} procedure - The procedure that may link a tab via `procedureTeamId`.
 * @returns {Readonly<ProcedureTeamContext>} The frozen resolved context.
 */
exports.getProcedureTab = getProcedureTab;

exports.createProcedureTeamContext = function(event, procedure) {
  if (event == null) {
    return Object.freeze(buildEventLevelContext(null));
  }

  if (!isPerProcedureTeamEnabled(event)) {
    return Object.freeze(buildEventLevelContext(event));
  }

  const tab = getProcedureTab(event, procedure);
  if (tab == null) {
    return Object.freeze(buildEventLevelContext(event));
  }

  return Object.freeze(buildTabContext(tab, event));
};
